import { EventListeners, MinzeAttrs, MinzeElement } from 'minze'
import { Font } from '../../constants'
import {
  AbstractSearchCore,
  AbstractSubscriber,
  IFacet,
  IFacetOption,
  ISearchConfig,
} from '../../modules/search'
import MainModule from '../../modules/main'
import {
  convertTimeRangeToFilterFacet,
  getTimeFromString,
  getTimeRangeFromTime,
  Time,
  TimeRange,
} from '../../modules/time'
import { Locale } from '../../modules/locale'

Locale.getInstance().registerKeySet('fr', 'discoveryResults', {
  title: 'Et voilà!',
  subtitle: 'Vos prochaines<br>sorties culturelles sont ici.',
  secondSubtitle: 'On vous en propose quelques-unes?',
  limitedResultsTitle: "Et voilà!<br>D'autres suggestions proches de ce que vous recherchez",
  noResultsTitle: "Oups!<br>Nous n'avons pas trouvé de suggestions pour vos critères.",
  notWhatLookingFor:
    "Vous n'avez pas trouvé l'activité parfaite?<br>Utilisez la recherche pour trouver des activités idéales!",
  moreResultsTitle:
    "Vous n'avez pas trouvé la sortie parfaite?<br>Utilisez la recherche pour trouver des événements qui vous conviennent.",
  seeMore: 'Lancer la recherche',
  goToSearch: 'Élargissez votre recherche',
  alternativesLabel: 'On a des options qui correspondent<br>à certains de vos critères:',
})

Locale.getInstance().registerKeySet('en', 'discoveryResults', {
  title: 'All done!',
  subtitle: 'Find your next cultural experiences here.',
  secondSubtitle: 'Want some suggestions?',
  limitedResultsTitle: 'All done!<br>Recommendations similar to your search',
  noResultsTitle: "Oops!<br>We can't find any suggestions that match your criteria",
  notWhatLookingFor:
    "You didn't find the perfect activity?<br>Use the search to find ideal activities!",
  moreResultsTitle:
    "Haven't found the perfect activity yet? <br> Use the search to find your ideal fit",
  seeMore: 'Start search',
  goToSearch: 'Broaden your search',
  alternativesLabel: 'We have options that match<br> some of your criteria:',
})

class DiscoveryResultsSubscriber extends AbstractSubscriber {
  private core: AbstractSearchCore | null = null
  private searchResultCallback: any
  constructor(groupId: string = 'discoverySearch') {
    super(groupId)
  }

  public setCallback(callback: any) {
    this.searchResultCallback = callback
  }

  public onSearchResults(results: any) {}

  async attached(searchCore: AbstractSearchCore): Promise<void> {
    this.core = searchCore
  }

  detaching() {}

  private async waitUntilReady(): Promise<void> {
    return new Promise((resolve, reject) => {
      let interval = setInterval(() => {
        if (this.core) {
          clearInterval(interval)
          resolve()
        }
      }, 100)
    })
  }

  public async doSearch(
    whereObj: any,
    whenObj: TimeRange,
    kindObj: string[],
    hitsPerPage?: number
  ): Promise<any> {
    await this.waitUntilReady()

    if (this.core) {
      let config: ISearchConfig = this.core.searchConfig

      if (config?.options?.hitsPerPage) {
        config.options.hitsPerPage.events_search = hitsPerPage || 6
      }

      if (whereObj && whereObj.latitude && whereObj.longitude) {
        if (config?.options?.aroundLatLng) {
          config.options.aroundLatLng.events_search = `${whereObj.latitude},${whereObj.longitude}`
        }
      }

      if (whenObj && config) {
        config.dateFilters = convertTimeRangeToFilterFacet(whenObj)
      }

      if (kindObj?.length > 0) {
        config.facets = config.facets ?? []
        let kindFacet: IFacet = {
          name: 'uiAdditionalTypesCode',
          options: [
            ...kindObj.map(f => {
              return {
                value: f,
                selected: true,
                available: true,
              }
            }),
          ] as IFacetOption[],
        }

        config.facets = [kindFacet]
      } else {
        config.facets = []
      }

      let results = await this.core.executeSearch(config)

      return results?.events_search?.items ?? []
    }
  }

  public async onSearchTextUpdate(text: string): Promise<void> {}

  public async onResultUpdate(result: any): Promise<void> {
    if (this.searchResultCallback) {
      this.searchResultCallback(result)
    }
  }

  public async goToSearchPage(): Promise<void> {
    this.core?.goToSearch()
  }

  public async onFacetUpdate(face: any): Promise<void> {}

  public async onSearchConfigUpdate(config: any): Promise<void> {}
}

export class LvcDiscoveryResults extends MinzeElement {
  private results: any[] = []
  private otherResults: any[] = []
  private subscriber: DiscoveryResultsSubscriber | null = null
  attrs: MinzeAttrs = [
    ['where-value', ''],
    ['when-value', ''],
    ['kind-value', ''],
  ]

  async onReady(): Promise<void> {
    this.subscriber = new DiscoveryResultsSubscriber()
    MainModule.getInstance().getSearchManager().subscribe(this.subscriber)
    let whereCriteria: any = null
    let whenCriteria: TimeRange = getTimeRangeFromTime(Time.ANYTIME)
    let kindCriteria: any = []

    if (
      this.whereValue &&
      this.whereValue != 'anywhere' &&
      this.whereValue != 'aroundMe' &&
      this.whereValue !== true
    ) {
      whereCriteria = await MainModule.getInstance()
        .getGeo()
        .getById(this.whereValue as string)
    } else if (this.whereValue == 'aroundMe') {
      whereCriteria = await MainModule.getInstance().getGeo().currentLocation
    }

    if (this.whenValue && this.whenValue != 'anytime' && this.whenValue !== true) {
      whenCriteria = getTimeRangeFromTime(getTimeFromString(this.whenValue as string))
    }

    if (this.kindValue && this.kindValue !== true) {
      kindCriteria = JSON.parse(decodeURIComponent(this.kindValue as string))
    }

    let results = await this.subscriber.doSearch(whereCriteria, whenCriteria, kindCriteria)
    let otherResults
    if (results.length <= 3) {
      otherResults = await this.subscriber.doSearch(
        null,
        getTimeRangeFromTime(getTimeFromString(Time.ANYTIME)),
        [],
        2
      )
    }

    this.results = results
    this.otherResults = otherResults
    this.rerender()
  }

  onSeeMoreClick() {
    this.subscriber?.goToSearchPage()
  }

  html = () => {
    return `
      <h1 class="title">
        ${Locale.getKey(
          !this.results || !this.results.length
            ? 'discoveryResults.noResultsTitle'
            : this.results?.length > 3
              ? 'discoveryResults.title'
              : 'discoveryResults.limitedResultsTitle'
        )}
      </h1>

      ${
        this.results?.length > 3
          ? ` 
      <lvc-text class="subtitle" font-preset="xLargeMediumBold" text-align="center">
      ${Locale.getKey('discoveryResults.subtitle')}
      </lvc-text>`
          : ``
      }


      ${
        this.results?.length > 3
          ? ` 
      <lvc-text font-preset="xLargeMediumBold" text-align="center">
      ${Locale.getKey('discoveryResults.secondSubtitle')}
      </lvc-text>`
          : ``
      }
     
      
      ${this.results.length > 3 ? `<lvc-mains-icon></lvc-mains-icon>` : ''}
      <lvc-search-result-block data-columns="3" data="${encodeURIComponent(JSON.stringify(this.results))}"></lvc-search-result-block>
      ${
        this.otherResults?.length
          ? `
        <div class="container">
          <h2 class="secondary-title">
            ${Locale.getKey('discoveryResults.alternativesLabel')}
          </h2>
          <lvc-search-result-block data-discovery-mode="true" data-columns="3" data="${encodeURIComponent(JSON.stringify(this.otherResults))}"></lvc-search-result-block>
        </div>
        <div class="get-more-results">
          <h4 class="secondary-title">${Locale.getKey('discoveryResults.moreResultsTitle')}</h4>
          <lvc-button
            class="see-more"
            width="auto"
            border="1px solid #1E007D"
            display="flex"
            align-items="center"
            theme="transparent"
            font-weight="${Font.weight.medium}"
          >
            ${Locale.getKey('discoveryResults.seeMore')} <lvc-search-icon></lvc-search-icon>
          </lvc-button>
        </div>
      `
          : ''
      }
      ${
        this.results.length > 3
          ? `
        <div class="container is-see-more">
          <p class="not-what-looking-for">${Locale.getKey('discoveryResults.notWhatLookingFor')}</p>
          <lvc-button enable-hover-box-shadow="true" class="see-more" font-weight="${Font.weight.medium}">${Locale.getKey('discoveryResults.seeMore')}</lvc-button>
        </div>
      `
          : ''
      }
    `
  }

  css = () => {
    return `
      :host {
        max-width: 1040px;
        width: 100%;
        margin: 0 auto;
      }
      .container {
        border-top: 2px solid #DDD;
        margin-top: 40px;
        padding-top: 40px;
      }
      .container.is-see-more {
        display: flex;
        flex-direction: column;
        align-items: center;
      }

      .subtitle{
        margin-bottom: 40px;
      }
      .title {
          text-align: center;
          font-size: 60px;
          font-weight: ${Font.weight.extraBold};
          line-height: 1;
          margin: 0 0 32px;
      }
      .secondary-title {
        text-align: center;
        font-size: 40px;
        line-height: 1;
        margin: 8px 0 48px 0;
        font-weight: ${Font.weight.extraBold};
      }
      lvc-mains-icon {
        text-align: center;
        margin-bottom: 40px;
      }
      lvc-search-icon {
        width: 24px;
        height: 24px;
        margin-left: 8px;
      }

      .not-what-looking-for {
        color: var(--brand-minuit, #1E007D);
        font-family: ${Font.family};
        text-align: center;
        font-variant-numeric: lining-nums tabular-nums;
        font-feature-settings: 'clig' off, 'liga' off;

        /* Body */
        font-size: 20px;
        font-style: normal;
        font-weight: ${Font.weight.medium};
        line-height: 150%; /* 30px */
      }
      
      .get-more-results {
        margin-top: 96px;
        display: flex;
        flex-direction: column;
        align-items: center;
      }
    @media screen and (max-width: 991px) {
      .title {
        font-size: 48px;
      }
      .secondary-title {
        font-size: 32px;
      }
    }
    @media screen and (max-width: 540px) {
      .title {
        font-size: 24px;
        margin-bottom: 8px;
      }
      .secondary-title {
        font-size: 20px;
        margin-bottom: 24px;
      }
      .container {
        padding-top: 24px;
        margin-top: 24px;
      }
      lvc-mains-icon {
        margin-bottom: 16px;
      }
      :host {
        overflow:auto;
      }
    }
    
      `
  }

  eventListeners: EventListeners = [['.see-more', 'click', this.onSeeMoreClick.bind(this)]]
}
